import { ADMINISTRATION_METHOD_OPTIONS } from "features/immunizations/constants/administrationMethods";

// Vaccination type for RSV — `healthlink_code_table_id` on the seeded products
// (api-practice `z_230_vaccines_rsv.sql`) and the name the backend matches with
// `VaccinationAdministrationType.fromName`. RSV emits no risk factors,
// additional factors or immunocompromised assessment, so those sections are
// hidden for it.
export { LEGACY_ADMINISTRATION_METHOD_CODES } from "features/immunizations/constants/administrationMethods";

export const RSV_VACCINE_TYPE = "RSV";

// The sites that do not contradict an intramuscular route. Nirsevimab is given into a
// limb — the anterolateral thigh in an infant — so the oral and nasal sites are not
// offered for it.
export const RSV_SITE_CODES = ["LT", "RT", "LUA", "RUA"];

export const VACCINE_TYPE_GROUPS = [
  {
    label: "COVID-19",
    options: [
      { label: "Covid-19 Booster", value: "Covid-19 Booster" },
      {
        label: "Covid-19 Immunocompromised",
        value: "Covid-19 Immunocompromised",
      },
      {
        label: "Covid-19 Primary/ Secondary",
        value: "Covid-19 Primary/ Secondary",
      },
    ],
  },
  {
    label: "Influenza",
    options: [{ label: "Influenza", value: "Influenza" }],
  },
  {
    label: "PCI",
    options: [
      {
        label: "4:1 (dip/tet/pertuss/polio)",
        value: "4:1 (dip/tet/pertuss/polio)",
      },
      { label: "6:1", value: "6:1" },
      {
        label: "Boostrix (dip/tet/pertuss)",
        value: "Boostrix (dip/tet/pertuss)",
      },
      { label: "Hepatitis A", value: "Hepatitis A" },
      { label: "Hib/ MenC", value: "Hib/ MenC" },
      { label: "Men B", value: "Men B" },
      { label: "Men C", value: "Men C" },
      { label: "MenACWY", value: "MenACWY" },
      { label: "MMR", value: "MMR" },
      { label: "MMRV", value: "MMRV" },
      { label: "PCV", value: "PCV" },
      { label: "Rotavirus oral vaccine", value: "Rotavirus oral vaccine" },
      { label: "Shingles", value: "Shingles" },
      { label: "Varicella", value: "Varicella" },
    ],
  },
  {
    label: "Pneumococcal",
    options: [{ label: "Pneumococcal", value: "Pneumococcal" }],
  },
  {
    label: "RSV",
    options: [{ label: "RSV", value: RSV_VACCINE_TYPE }],
  },
];

export const PCI_VACCINE_OPTIONS = VACCINE_TYPE_GROUPS.find(
  (g) => g.label === "PCI"
).options;

export const VACCINE_TYPES = [
  "4:1 (dip/tet/pertuss/polio)",
  "6:1",
  "Boostrix (dip/tet/pertuss)",
  "Covid-19 Booster",
  "Covid-19 Immunocompromised",
  "Covid-19 Primary/ Secondary",
  "Hepatitis A",
  "Hib/ MenC",
  "Influenza",
  "Men B",
  "Men C",
  "MenACWY",
  "MMR",
  "MMRV",
  "PCV",
  "Pneumococcal",
  "Rotavirus oral vaccine",
  RSV_VACCINE_TYPE,
  "Shingles",
  "Varicella",
];

export {
  LEGACY_SITE_GIVEN_CODES,
  SITE_OPTIONS,
} from "features/immunizations/constants/administrationSites";

// The four routes this form offers, drawn from the shared list. Intravenous and
// intradermal are in that list because legacy records carry them and table 0162 has
// them, but no vaccine we record is given either way, so the picker does not offer them.
export const ADMINISTRATION_METHODS = ADMINISTRATION_METHOD_OPTIONS.filter((method) =>
  ["IM", "IN", "PO", "SC"].includes(method.value)
);


export const ADMINISTRATION_NOTES_CODE_OPTIONS = [
  { value: "X0070-1", label: "Dose completes course" },
  { value: "X0070-2", label: "Dose does not complete course" },
  { value: "X0070-3", label: "Dose is clinically necessary" },
  { value: "X0070-4", label: "Dose is not clinically necessary" },
];

export const DOSE_COURSE_OPTIONS = [
  { value: "X0070-1", label: "Dose completes course" },
  { value: "X0070-2", label: "Dose does not complete course" },
];

export const CLINICAL_NECESSITY_OPTIONS = [
  { value: "yes", label: "Clinically necessary" },
  { value: "no", label: "Not clinically necessary" },
];

// The only reaction value a reaction date can accompany. "No" contradicts a date
// outright, and "Unknown" — the default — cannot be reconciled with one either: we
// either do not know whether there was a reaction, or we know when it happened.
export const REACTION_YES = "YES";

export const REACTION_OPTIONS = [
  { value: "UNKNOWN", label: "Unknown" },
  { value: "YES", label: "Yes" },
  { value: "NO", label: "No" },
];

export const CONSENT_OPTIONS = [
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" },
  { value: "BI", label: "Best Interest" },
];

export const MANUFACTURER_OPTIONS = [
  { value: "AB", label: "Abbott Laboratories", enabled: true },
  { value: "ACA", label: "Acambis, Inc", enabled: true },
  { value: "AD", label: "Adams Laboratories, Inc.", enabled: true },
  { value: "AKR", label: "Akorn, Inc", enabled: true },
  { value: "ALP", label: "Alpha Therapeutic Corporation", enabled: true },
  { value: "AR", label: "Armour", enabled: true },
  { value: "ASZ", label: "AstraZeneca", enabled: true },
  { value: "AVB", label: "Aventis Behring L.L.C.", enabled: true },
  { value: "AVI", label: "Aviron", enabled: true },
  {
    value: "BA",
    label: "Baxter Healthcare Corporation-inactive",
    enabled: true,
  },
  { value: "BAH", label: "Baxter Healthcare Corporation", enabled: true },
  { value: "BAY", label: "Bayer Corporation", enabled: true },
  {
    value: "BBI",
    label: "Bharat Biotech International Limited",
    enabled: true,
  },
  { value: "BN", label: "Bavarian Nordic A/S", enabled: true },
  { value: "BP", label: "Berna Products", enabled: true },
  { value: "BPC", label: "Berna Products Corporation", enabled: true },
  { value: "BRR", label: "Barr Laboratories", enabled: true },
  { value: "BTP", label: "Biotest Pharmaceuticals Corporation", enabled: true },
  { value: "CAN", label: "CanSino Biologics, Inc", enabled: true },
  { value: "CEN", label: "Centeon L.L.C.", enabled: true },
  { value: "CHI", label: "Chiron Corporation", enabled: true },
  { value: "CMP", label: "Celltech Medeva Pharmaceuticals", enabled: true },
  { value: "CNJ", label: "Cangene Corporation", enabled: true },
  { value: "CON", label: "Connaught", enabled: true },
  { value: "CRU", label: "Crucell", enabled: true },
  { value: "CSL", label: "bioCSL", enabled: true },
  { value: "DSI", label: "Dispensing Solutions", enabled: true },
  { value: "DVC", label: "DynPort Vaccine Company, LLC", enabled: true },
  { value: "DVX", label: "Dynavax, Inc.", enabled: true },
  { value: "DYN", label: "Dynaport", enabled: true },
  { value: "EVN", label: "Evans Medical Limited", enabled: true },
  { value: "GEO", label: "GeoVax Labs, Inc.", enabled: true },
  { value: "GRE", label: "Greer Laboratories, Inc.", enabled: true },
  { value: "GRF", label: "Grifols", enabled: true },
  { value: "IAG", label: "Immuno International AG", enabled: true },
  { value: "IDB", label: "ID Biomedical", enabled: true },
  { value: "IM", label: "Merieux", enabled: true },
  { value: "INT", label: "Intercell Biomedical", enabled: true },
  { value: "IUS", label: "Immuno-U.S., Inc.", enabled: true },
  { value: "JNJ", label: "Johnson and Johnson", enabled: true },
  {
    value: "JPN",
    label:
      "The Research Foundation for Microbial Diseases of Osaka University (BIKEN)",
    enabled: true,
  },
  { value: "JSN", label: "Janssen", enabled: true },
  { value: "KED", label: "Kedrion Biopharma", enabled: true },
  { value: "KGC", label: "Korea Green Cross Corporation", enabled: true },
  { value: "LED", label: "Lederle", enabled: true },
  {
    value: "MA",
    label: "Massachusetts Public Health Biologic Laboratories",
    enabled: true,
  },
  { value: "MBL", label: "Massachusetts Biologic Laboratories", enabled: true },
  { value: "MCM", label: "MCM Vaccine Company", enabled: true },
  { value: "MDO", label: "Medicago, Inc", enabled: true },
  { value: "MED", label: "MedImmune, Inc. (AstraZeneca)", enabled: true },
  { value: "MIL", label: "Miles", enabled: true },
  { value: "MIP", label: "Emergent BioSolutions", enabled: true },
  { value: "MOD", label: "Moderna US, Inc.", enabled: true },
  { value: "MSD", label: "Merck and Co., Inc.", enabled: true },
  {
    value: "MSP",
    label: "MSP Vaccine Company - (partnership Merck and Sanofi Pasteur)",
    enabled: true,
  },
  { value: "NAB", label: "NABI", enabled: true },
  { value: "NAV", label: "North American Vaccine, Inc.", enabled: true },
  { value: "NOV", label: "Novartis Pharmaceutical Corporation", enabled: true },
  { value: "NVX", label: "Novavax, Inc.", enabled: true },
  { value: "NYB", label: "New York Blood Center", enabled: true },
  { value: "ORT", label: "Ortho-clinical Diagnostics", enabled: true },
  { value: "OTC", label: "Organon Teknika Corporation", enabled: true },
  { value: "OTH", label: "Other manufacturer", enabled: true },
  {
    value: "PAX",
    label: "Emergent Travel Health, Inc (Formerly PaxVax)",
    enabled: true,
  },
  { value: "PD", label: "Parkedale Pharmaceuticals", enabled: true },
  { value: "PFR", label: "Pfizer, Inc", enabled: true },
  { value: "PMC", label: "Sanofi Pasteur", enabled: true },
  { value: "PRX", label: "Praxis Biologics", enabled: true },
  { value: "PSC", label: "Protein Sciences", enabled: true },
  { value: "PWJ", label: "PowderJect Pharmaceuticals", enabled: true },
  { value: "REB", label: "Rebel Distributors", enabled: true },
  { value: "SCL", label: "Sclavo, Inc.", enabled: true },
  { value: "SEQ", label: "Seqirus", enabled: true },
  { value: "SI", label: "Swiss Serum and Vaccine Inst.", enabled: true },
  { value: "SKB", label: "GlaxoSmithKline", enabled: true },
  { value: "SNV", label: "Sinovac", enabled: true },
  { value: "SOL", label: "Solvay Pharmaceuticals", enabled: true },
  { value: "SPH", label: "Sinopharm-Biotech", enabled: true },
  { value: "TAL", label: "Talecris Biotherapeutics", enabled: true },
  { value: "TVA", label: "TEVA Pharmaceuticals USA", enabled: true },
  { value: "UNK", label: "Unknown manufacturer", enabled: true },
  {
    value: "USA",
    label: "United States Army Medical Research and Material Command",
    enabled: true,
  },
  { value: "VAL", label: "Valneva", enabled: true },
  { value: "VBI", label: "VBI Vaccines, Inc", enabled: true },
  {
    value: "VET",
    label: "Vetter Pharma Fertigung GmbH & Co. KG",
    enabled: true,
  },
  { value: "VXG", label: "VaxGen", enabled: true },
  { value: "WA", label: "Wyeth-Ayerst", enabled: true },
  { value: "WAL", label: "Wyeth", enabled: true },
  { value: "ZLB", label: "ZLB Behring", enabled: true },
];

export const getManufacturerByMvxCode = (mvxCode) => {
  if (!mvxCode) return null;
  const manufacturer = MANUFACTURER_OPTIONS.find((m) => m.value === mvxCode);
  return manufacturer || { value: mvxCode, label: mvxCode, enabled: true };
};

export const RISK_FACTOR_DESCRIPTIONS = {
  // ── Flu + Pneumo ────────────────────────────────────────────────────────
  "X0069-A": "Chronic Respiratory Disease",
  "X0069-B": "Pregnant",
  "X0069-C": "Chronic Heart Disease",
  "X0069-D": "Chronic Renal Failure",
  "X0069-E": "Chronic Liver Disease",
  "X0069-F": "Chronic Neurological Disease",
  "X0069-G": "Immunosuppressed",
  "X0069-H": "Household contacts/carer",
  "X0069-I": "Diabetes Mellitus",
  "X0069-J": "BMI > 40",
  "X0069-K": "Haemoglobinopathies",
  "X0069-M": "Nursing home / long stay resident",
  "X0069-O": "Carers",
  "X0069-P": "Close contact pigs/poultry",
  "X0069-Q": "Children on long-term aspirin",
  "X0069-R": "HCW - Medical/Dental",
  "X0069-S": "HCW - Nursing",
  "X0069-T": "HCW - Health + Social Care",
  "X0069-U": "HCW - Management/Admin",
  "X0069-V": "HCW - General Support",
  "X0069-W": "Other HCW",
  "X0069-X": "Age 65 and over",
  "X0069-AL": "Down Syndrome",
  // ── Pneumo-only ─────────────────────────────────────────────────────────
  "X0069-Y": "Children <5 invasive pulmonary",
  "X0069-Z": "CSF leak",
  "X0069-AA": "Pre/post Cochlear implant",
  "X0069-02": "Occupational exposure to metal fumes",
  "X0069-03": "Smokers and Alcoholics",
  // ── Flu-only numeric codes ───────────────────────────────────────────────
  "X0069-01": "Aged 2 to 17 years",
  "X0069-09": "Adult compromised respiratory function",
  "X0069-13": "Aged 60 to 64",
  "X0069-15": "Children with neurodevelopmental disorders",
  // ── Other active codes ───────────────────────────────────────────────────
  "X0069-05": "Inherited metabolic diseases",
  "X0069-08": "Severe mental illness",
  "X0069-16": "Aged 18-59 years",
  // ── Inactive / legacy — kept for display of older records ────────────────
  "X0069-AN": "Aged 2 to 12 years",
  "X0069-06": "Aged 50 to 64",
  "X0069-11": "Aged 18 to 64 years",
  "X0069-12": "Aged 12-17 (1st Booster Only)",
  "X0069-14": "Aged 6 months to 4 years (IC)",
  "X0069-04": "BMI > 35",
  "X0069-07": "Intellectual disability (excl. Down Syndrome)",
  "X0069-10": "Aged 5-11 years (IC)",
  "X0069-L": "Child compromised respiratory function",
};

// Products whose course is a single dose, so the only correct dose number is 1
// (KIN-1708). `X0063-0` Shot Number carries this on the wire, and every single-dose
// sample in Vaccinations Specification v4.8.4 sends 1 — the rule is not "always 1" but
// "a single-dose course can only be dose 1". The 4-in-1 stands in for a 6-in-1 dose 4
// and is deliberately not here.
//
// Mirrors SINGLE_DOSE_CODES in the backend's HealthlinkValidationService, which is a
// duplication: the two lists will drift the moment a product is added on one side only.
// A flag on VaccineCodeDTO would settle it — VaccineCodeDTO carries no notion of course
// length today. Asked for on the ticket.
//
// Note the backend's own isSingleDose() is wider than this list: influenza counts as
// single-dose from age 9, and PPV23 when a prior dose is under five years old. Both are
// a function of the patient, not the product, so neither is decidable from the picker
// and neither is in this ticket's scope.
export const SINGLE_DOSE_COURSE_CODES = [
  "212", // Jcovden (Janssen) — single-dose COVID
  "012", // Comirnaty — single-dose COVID
  "187431000220104", // MenC
  "631531000220102", // Hib/MenC
  "950271000220106", // Beyfortus 50mg (<5kg) — RSV
  "950311000220106", // Beyfortus 100mg (>=5kg) — RSV
];

// Influenza is a single-dose course from age nine — a function of the patient at the
// date given, not of the product, so it cannot be read off the picker alone. Mirrors
// INFLUENZA_CODES in HealthlinkValidationService, whose isSingleDose() the backend's
// HARD_STOP gates on.
export const INFLUENZA_COURSE_CODES = [
  "149", // Fluenz Tetra
  "150", // Quadrivalent Influenza Vaccine
  "111", // Fluenz Nasal
  "185", // Influvac Tetra
  "010", // Vaxigrip
  "011", // Influvac-TIV
];

export const INFLUENZA_SINGLE_DOSE_FROM_AGE_YEARS = 9;

// Pneumovax 23. A further dose is a single-dose course when the previous one is inside
// this window — a function of what the patient already has, not of the product. Mirrors
// PPV23_CODE and the 1825-day comparison in HealthlinkValidationService.isSingleDose.
export const PPV23_CODE = "33";
export const PPV23_SINGLE_DOSE_WINDOW_DAYS = 1825;

// What a single-dose course is offered instead of the sequence positions. Sends 1,
// because that is the value NIIS expects; the label exists so a clinician is not asked
// to pick a position in a sequence that does not exist.
export const SINGLE_DOSE_NUMBER_OPTIONS = [
  { value: "1", label: "Single dose" },
];

export const DOSE_NUMBER_OPTIONS = [
  { value: "1", label: "Primary Course - Dose 1" },
  { value: "2", label: "Primary Course - Dose 2" },
  { value: "3", label: "Primary Course - Dose 3" },
  { value: "4", label: "Primary Course - Dose 4" },
  { value: "31", label: "Primary Course - Additional Dose" },
  { value: "51", label: "Booster Course - Booster Dose" },
];

export const PCI_SCHEDULE_CODE_OPTIONS = [
  { value: "X0072-1", label: "PCI 2016" },
  { value: "X0072-2", label: "PCI 2024" },
];

export const DEFAULT_VALUES = {
  administrationMethod: "IM",
  siteGiven: "LT",
  reaction: "UNKNOWN",
};

export const getRiskFactorDescription = (code) => {
  return RISK_FACTOR_DESCRIPTIONS[code] || code;
};
